import { createWelcomeStep, createMiddleStep, createFinalStep } from '../config/stepTemplates';
import { waitForElement, waitForReduxState, fillReactInput } from '../utils/tourUtils';
import { updateCountryPreference } from '../../../redux/actions/userAction';
import { clearDocument } from '../../../redux/actions/documentAction';

const TOTAL = 13;

export function getChatTourSteps(t, { dispatch, store, regionContent }) {
  return [
    // 1 — Welcome (centered, no target) — close any open document
    {
      ...createWelcomeStep({
        id: 'chat-welcome',
        title: t('onboarding_tour.chat.step1.title', 'AI Legal Assistant'),
        text: t('onboarding_tour.chat.step1.text', 'Your personal AI legal assistant. Ask any legal question and get a sourced, detailed answer backed by real laws and court decisions.'),
        t,
        totalSteps: TOTAL,
      }),
      when: {
        show() {
          dispatch(clearDocument());
        },
      },
    },

    // 2 — Click "New Chat" (advanceOn click)
    {
      id: 'chat-new-conversation',
      title: t('onboarding_tour.chat.step2.title', 'Create a Conversation'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((2 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 2, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.chat.step2.text', 'Click here to start a new conversation. Each conversation keeps its own history and context.')}</p>`,
      attachTo: { element: '.create-conversation-btn', on: 'right' },
      advanceOn: { selector: '.create-conversation-btn', event: 'click' },
      beforeShowPromise() {
        return waitForElement('.create-conversation-btn', 5000).catch(() => {});
      },
    },

    // 3 — Upload file
    {
      ...createMiddleStep({
        id: 'chat-upload',
        title: t('onboarding_tour.chat.step_upload.title', 'Upload Documents'),
        text: t('onboarding_tour.chat.step_upload.text', 'Attach contracts, legal documents, or any file for the AI to analyze. The AI will read and reference them in its answer.'),
        attachTo: { element: '[data-tour="chat-upload-btn"]', on: 'top' },
        t,
        stepIndex: 3,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="chat-upload-btn"]', 5000).catch(() => {});
      },
    },

    // 4 — Country selector
    {
      ...createMiddleStep({
        id: 'chat-country',
        title: t('onboarding_tour.chat.step5.title', 'Select Countries'),
        text: t(`onboarding_tour.chat.step5.text_${regionContent.countryCode}`,
          `Choose which countries' laws to search. The AI will search ${regionContent.countryName} laws and court decisions.`),
        attachTo: { element: '[data-tour="chat-country-selector"]', on: 'top' },
        t,
        stepIndex: 4,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="chat-country-selector"]', 3000).catch(() => {});
      },
    },

    // 5 — Mode selector
    {
      ...createMiddleStep({
        id: 'chat-mode',
        title: t('onboarding_tour.chat.step4.title', 'Choose Search Mode'),
        text: t('onboarding_tour.chat.step4.text', 'Select which sources the AI should consult: Laws (📜), Regulations (📋), Case Law (⚖️), or any combination.'),
        attachTo: { element: '[data-tour="chat-mode-selector"]', on: 'top' },
        t,
        stepIndex: 5,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="chat-mode-selector"]', 3000).catch(() => {});
      },
    },

    // 6 — Send a message (auto-fill + send)
    {
      id: 'chat-send-message',
      title: t('onboarding_tour.chat.step6.title', 'Send a Message'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((6 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 6, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.chat.step6.text', "Let's try it! Type a question or we'll suggest one. Then press Enter or click Send.")}</p>`,
      attachTo: { element: '.message-input', on: 'top' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        {
          text: t('onboarding_tour.send', 'Send →'),
          classes: 'shepherd-button-primary lawis-chat-send-btn',
          action: function () {
            const btn = document.querySelector('.lawis-chat-send-btn');
            if (btn) {
              btn.textContent = '⏳ ' + t('onboarding_tour.sending', 'Sending...');
              btn.style.pointerEvents = 'none';
              btn.style.opacity = '0.7';
            }
            const sendBtn = document.querySelector('[data-tour="chat-send-btn"]');
            if (sendBtn) sendBtn.click();
            this.next();
          },
        },
      ],
      when: {
        show() {
          dispatch(updateCountryPreference([regionContent.countryCode]));
          const textarea = document.querySelector('.message-input textarea');
          if (textarea && !textarea.value) {
            fillReactInput('.message-input textarea', regionContent.chatDemoQuery);
          }
        },
      },
    },

    // 7 — Execution steps
    {
      id: 'chat-execution-steps',
      title: t('onboarding_tour.chat.step7.title', 'AI is Researching'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((7 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 7, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.chat.step7.text', "Watch the AI's reasoning in real-time. Each step shows what it's researching — understanding your query, planning a strategy, searching documents, and synthesizing an answer.")}</p>`,
      attachTo: { element: '[data-tour="chat-execution-steps"]', on: 'bottom' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        { text: t('onboarding_tour.next', 'Next'), action: 'next', classes: 'shepherd-button-primary' },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="chat-execution-steps"]', 30000).catch(() => {});
      },
    },

    // 8 — Final answer
    {
      id: 'chat-final-answer',
      title: t('onboarding_tour.chat.step9.title', 'Your Answer'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((8 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 8, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.chat.step9.text', "Here's the complete legal analysis, synthesized from all research steps and backed by cited sources.")}</p>`,
      attachTo: { element: '[data-tour="chat-answer-content"]', on: 'bottom' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        { text: t('onboarding_tour.next', 'Next'), action: 'next', classes: 'shepherd-button-primary' },
      ],
      beforeShowPromise() {
        return waitForReduxState(
          store,
          (s) => {
            // Ensure streaming is fully done AND answer element exists in DOM
            const streamingId = s.conversations?.streamingMessageId;
            if (streamingId) return false;
            const loadingMessage = s.conversations?.loadingMessage;
            if (loadingMessage) return false;
            const msgs = s.conversations?.messages || [];
            const assistant = [...msgs].reverse().find((m) => m.role === 'assistant');
            return assistant && !assistant.is_streaming && assistant.content?.length > 100
              && document.querySelector('[data-tour="chat-answer-content"]');
          },
          60000
        ).catch(() => {});
      },
    },

    // 9 — Expand sources + focus on first source
    {
      id: 'chat-expand-sources',
      title: t('onboarding_tour.chat.step_sources.title', 'Legal Sources'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((9 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 9, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.chat.step_sources.text', 'Every answer is backed by real legal sources. Here you can see each cited document, its type, and the exact section used.')}</p>`,
      attachTo: { element: '[data-tour="chat-citations-panel"]', on: 'top' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        { text: t('onboarding_tour.next', 'Next'), action: 'next', classes: 'shepherd-button-primary' },
      ],
      beforeShowPromise() {
        // Click the sources chip to expand citations
        return waitForElement('[data-tour="chat-sources-chip"]', 5000)
          .then(() => {
            const chip = document.querySelector('[data-tour="chat-sources-chip"]');
            if (chip) chip.click();
            return waitForElement('[data-tour="chat-citations-panel"]', 3000);
          })
          .then((el) => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return new Promise((r) => setTimeout(r, 400));
          })
          .catch(() => {});
      },
    },

    // 10 — Open a source document — click "View Document" link, auto-advance when doc viewer appears
    {
      id: 'chat-open-source',
      title: t('onboarding_tour.chat.step_open_source.title', 'Open Source Document'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((10 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 10, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.chat.step_open_source.text', "Click 'View Document' on any source to read the full legal text in the side panel. The cited section will be highlighted.")}</p>`,
      attachTo: { element: '[data-tour="chat-citations-panel"]', on: 'top' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        { text: t('onboarding_tour.next', 'Next'), action: 'next', classes: 'shepherd-button-primary' },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="chat-citations-panel"]', 3000).catch(() => {});
      },
      when: {
        show() {
          // Watch for document viewer to open and auto-advance
          waitForElement('[data-tour="chat-doc-viewer"]', 30000)
            .then(() => {
              const step = this;
              setTimeout(() => {
                if (step.tour.getCurrentStep()?.id === 'chat-open-source') {
                  step.tour.next();
                }
              }, 500);
            })
            .catch(() => {});
        },
      },
    },

    // 11 — Document viewer (show the loaded document)
    {
      id: 'chat-docviewer',
      title: t('onboarding_tour.chat.step_docviewer.title', 'Document Viewer'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((11 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 11, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.chat.step_docviewer.text', 'The full legal document is displayed here with the cited section highlighted. You can scroll through the entire text.')}</p>`,
      attachTo: { element: '[data-tour="chat-doc-viewer"]', on: 'left' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        { text: t('onboarding_tour.next', 'Next'), action: 'next', classes: 'shepherd-button-primary' },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="chat-doc-viewer"]', 5000).catch(() => {});
      },
    },

    // 12 — Translation
    {
      id: 'chat-translate',
      title: t('onboarding_tour.chat.step_translate.title', 'Translate Documents'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((12 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 12, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.chat.step_translate.text', 'Translate any source document into 15+ languages. Select a language from the dropdown to see the translation in real-time.')}</p>`,
      attachTo: { element: '.htmlviewer-translate-select', on: 'bottom' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        { text: t('onboarding_tour.next', 'Next'), action: 'next', classes: 'shepherd-button-primary' },
      ],
      beforeShowPromise() {
        return waitForElement('.htmlviewer-translate-select', 5000).catch(() => {});
      },
    },

    // 13 — Final step — invite to explore
    createFinalStep({
      id: 'chat-explore',
      title: t('onboarding_tour.chat.step_explore.title', 'Keep Exploring'),
      text: t('onboarding_tour.chat.step_explore.text', 'You\'re all set! Continue the conversation, ask follow-up questions, or start a new one. The AI remembers the context of each conversation.'),
      t,
      totalSteps: TOTAL,
    }),
  ];
}
